import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";
import queryString from "query-string";
import { Trans, withTranslation } from "react-i18next";
import FloatingAlert from "../../components/FloatingAlert/FloatingAlert";
import bgSliders from "../../helpers/bgSliders";
import DjzSignupPageDisplay from "./DjzSignupPage.styled";
import "./DjzSignupPage.scss";

const API_ROOT = "https://vas2.grand-hub.com/api/playIt";
const SUBSCRIBE_BUTTON_ID = "sub_but";
const COUNTRY_CODE = "213";

const PACKAGES = {
  d: "djezzy-telenity-d",
  w: "djezzy-telenity-w",
};

const PLAN_DETAILS = {
  d: {
    price: 30,
    sub: "PIJ",
    unsub: "QuitPI",
    shortcode: "734",
    offerNameKey: "djz_offer_daily",
  },
  w: {
    price: 100,
    sub: "PIM",
    unsub: "QuitPI",
    shortcode: "734",
    offerNameKey: "djz_offer_weekly",
  },
};

function getApiBase(plan) {
  return `${API_ROOT}/${PACKAGES[plan] || PACKAGES.d}`;
}

function getPlanFromQuery(search) {
  const params = queryString.parse(search);
  const plan = params.plan;

  if (plan === "w" || plan === "d") {
    return plan;
  }

  return "d";
}

function getPhoneFromQuery(search) {
  const params = queryString.parse(search);
  const phone = params.phone;

  if (!phone || Array.isArray(phone)) {
    return "";
  }

  return String(phone).replace(/[^0-9]/g, "");
}

function toLocalPhone(phone) {
  let digits = String(phone || "").replace(/[^0-9]/g, "");

  if (digits.startsWith(COUNTRY_CODE)) {
    digits = digits.slice(COUNTRY_CODE.length);
  }

  if (digits.startsWith("0")) {
    digits = digits.slice(1);
  }

  return digits;
}

function toApiPhone(phone) {
  let digits = String(phone || "").replace(/[^0-9]/g, "");

  if (!digits) {
    return "";
  }

  if (digits.startsWith("0")) {
    digits = digits.slice(1);
  }

  if (digits.startsWith(COUNTRY_CODE)) {
    return digits;
  }

  return `${COUNTRY_CODE}${digits}`;
}

function hasPhoneQueryParam(search) {
  const params = queryString.parse(search);
  return Object.prototype.hasOwnProperty.call(params, "phone");
}

function getInitFlow(search) {
  const phoneFromQuery = getPhoneFromQuery(search);

  // Phone has a value → autofill + directSub.
  if (phoneFromQuery) {
    return "directSub";
  }

  // phone= is present but empty → skip HE, user fills phone manually.
  if (hasPhoneQueryParam(search)) {
    return "manual";
  }

  const params = queryString.parse(search);
  const hasOtherQuery = Object.keys(params).some((key) => {
    if (key === "phone" || key === "plan") {
      return false;
    }

    const value = params[key];
    return value !== undefined && value !== null && String(value).trim() !== "";
  });

  // Extra query (not phone / not plan) → antifraud + manual phone entry.
  if (hasOtherQuery) {
    return "manual";
  }

  // No query, or only plan → HE first.
  return "he";
}

const randomBg = bgSliders.getRandomBg();

function getDirectSubTokens(buttonId, storedTi, storedTs) {
  const button = document.getElementById(buttonId);

  return {
    ti:
      (button && (button.getAttribute("ti") || button.dataset.ti)) ||
      storedTi ||
      "",
    ts:
      (button && (button.getAttribute("ts") || button.dataset.ts)) ||
      storedTs ||
      "",
  };
}

function extractHeUrl(data) {
  if (!data) {
    return "";
  }

  const payload = data.data || data;
  const url =
    payload.url ||
    payload.redirectUrl ||
    payload.redirect_url ||
    data.url ||
    data.redirectUrl ||
    data.redirect_url;

  if (!url || typeof url !== "string") {
    return "";
  }

  const trimmed = url.trim();
  if (!/^https?:\/\//i.test(trimmed)) {
    return "";
  }

  return trimmed;
}

function getTransactionId(data) {
  if (!data) {
    return "";
  }

  return (
    data.transactionId ||
    data.transaction_id ||
    (data.data && (data.data.transactionId || data.data.transaction_id)) ||
    ""
  );
}

function getErrorMessage(error, fallback) {
  if (error && error.response && error.response.data) {
    const data = error.response.data;
    return data.message || data.error || fallback;
  }

  return (error && error.message) || fallback;
}

function isApiSuccess(data) {
  if (!data) {
    return false;
  }

  if (
    data.success === true ||
    data.status === true ||
    data.status === "success"
  ) {
    return true;
  }

  if (data.data) {
    return (
      data.data.success === true ||
      data.data.status === true ||
      data.data.status === "success"
    );
  }

  return false;
}

function DjzSignupPage({ t, i18n }) {
  const history = useHistory();
  const queryPhone = toLocalPhone(getPhoneFromQuery(window.location.search));
  const [phone, setPhone] = useState(queryPhone);
  const [isDirectSubFlow, setIsDirectSubFlow] = useState(Boolean(queryPhone));
  const [packagePlan, setPackagePlan] = useState(() =>
    getPlanFromQuery(window.location.search)
  );
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [antifraudTi, setAntifraudTi] = useState("");
  const [antifraudTs, setAntifraudTs] = useState("");
  const [showVerifyStep, setShowVerifyStep] = useState(false);
  const [otp, setOtp] = useState("");
  const [pendingTransactionId, setPendingTransactionId] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [showError, setShowError] = useState(false);

  const language =
    (i18n && i18n.language) || localStorage.getItem("i18nextLng") || "fr";
  const selectedPlan = PLAN_DETAILS[packagePlan] || PLAN_DETAILS.d;

  const changeLanguage = (lng) => {
    if (!i18n || !lng) return;
    i18n.changeLanguage(lng);
    localStorage.setItem("i18nextLng", lng);

    const url = new URL(window.location.href);
    url.searchParams.set("lang", lng);
    window.history.pushState({}, "", url.toString());
  };

  const changePackagePlan = (plan) => {
    setPackagePlan(plan);

    const url = new URL(window.location.href);
    url.searchParams.set("plan", plan);
    window.history.pushState({}, "", url.toString());
  };

  useEffect(() => {
    const params = queryString.parse(window.location.search);
    if (params.lang) {
      localStorage.setItem("i18nextLng", params.lang);
      if (localStorage.getItem("i18nextLng") !== params.lang) {
        localStorage.setItem("i18nextLng", params.lang);
        window.location.reload();
      }
    }
  }, []);

  useEffect(() => {
    initializePage(packagePlan);
  }, [packagePlan]);

  const loadAntifraudScript = async (apiBase) => {
    const res = await axios.get(
      `${apiBase}/antifraud?targetElement=${SUBSCRIBE_BUTTON_ID}`
    );

    const payload = res.data.data || res.data;
    const script = payload && payload.script;

    if (script) {
      const addScript = document.createElement("script");
      addScript.innerHTML = script;
      document.head.appendChild(addScript);

      const event = new Event("DCBProtectRun");
      document.dispatchEvent(event);
    }

    if (payload && payload.ti) {
      setAntifraudTi(payload.ti);
    }
    if (payload && payload.ts) {
      setAntifraudTs(payload.ts);
    }
  };

  const loadHeaderEnrichment = async (apiBase) => {
    const res = await axios.get(`${apiBase}/he`);
    const heUrl = extractHeUrl(res.data);

    if (heUrl) {
      window.location.href = heUrl;
      return true;
    }

    return false;
  };

  const initializePage = async (plan) => {
    const apiBase = getApiBase(plan);
    const phoneFromQuery = getPhoneFromQuery(window.location.search);
    const initFlow = getInitFlow(window.location.search);

    setInitializing(true);
    setShowError(false);
    setAntifraudTi("");
    setAntifraudTs("");
    setShowVerifyStep(false);
    setOtp("");
    setPendingTransactionId("");

    try {
      // Phone in query: call antifraud, autofill phone, use directSub.
      if (initFlow === "directSub") {
        setPhone(toLocalPhone(phoneFromQuery));
        setIsDirectSubFlow(true);

        try {
          await loadAntifraudScript(apiBase);
        } catch (error) {
          console.error("Djz antifraud failed:", error);
        }
        return;
      }

      setPhone("");
      setIsDirectSubFlow(false);

      // No query or only plan: call HE first (no antifraud before HE).
      if (initFlow === "he") {
        try {
          const redirected = await loadHeaderEnrichment(apiBase);
          if (redirected) {
            return;
          }
        } catch (error) {
          console.error("Djz HE failed:", error);
        }

        // HE did not redirect → manual fallback, load antifraud for verify tokens.
        try {
          await loadAntifraudScript(apiBase);
        } catch (error) {
          console.error("Djz antifraud failed:", error);
        }

        setPhone("");
        setIsDirectSubFlow(false);
        return;
      }

      // Other query (not phone / not plan): antifraud + user fills phone.
      try {
        await loadAntifraudScript(apiBase);
      } catch (error) {
        console.error("Djz antifraud failed:", error);
      }

      setPhone("");
      setIsDirectSubFlow(false);
    } finally {
      setInitializing(false);
    }
  };

  const goToThankYou = () => {
    history.push("/djz-success");
  };

  const handleDirectSubscribe = async (apiBase, phoneNumber, ti, ts) => {
    const res = await axios.post(`${apiBase}/directSub`, {
      phone: phoneNumber,
      ti,
      ts,
    });

    if (isApiSuccess(res.data)) {
      goToThankYou();
      return;
    }

    throw new Error(res.data.message || res.data.error || "request_failed");
  };

  const handleManualSubscribe = async (apiBase, phoneNumber) => {
    const subscribeRes = await axios.post(`${apiBase}/subscribe`, {
      phone: phoneNumber,
    });

    if (!isApiSuccess(subscribeRes.data)) {
      throw new Error(
        subscribeRes.data.message ||
          subscribeRes.data.error ||
          "request_failed"
      );
    }

    const transactionId = getTransactionId(subscribeRes.data);
    if (!transactionId) {
      throw new Error("request_failed");
    }

    setPendingTransactionId(transactionId);
    setShowVerifyStep(true);
    setOtp("");
  };

  const handleVerifyOtp = async (apiBase, phoneNumber) => {
    if (!otp) {
      setErrorMsg(t("otpRequired"));
      setShowError(true);
      return;
    }

    const { ti, ts } = getDirectSubTokens(
      SUBSCRIBE_BUTTON_ID,
      antifraudTi,
      antifraudTs
    );

    const verifyRes = await axios.post(`${apiBase}/verify`, {
      phone: phoneNumber,
      transactionId: pendingTransactionId,
      otp,
      ti,
      ts,
    });

    if (isApiSuccess(verifyRes.data)) {
      goToThankYou();
      return;
    }

    throw new Error(
      verifyRes.data.message || verifyRes.data.error || "verifyFailed"
    );
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setShowError(false);
    setErrorMsg("");

    if (!phone) {
      setErrorMsg(t("Empty Phone Number Or Password"));
      setShowError(true);
      return;
    }

    const apiBase = getApiBase(packagePlan);
    const apiPhone = toApiPhone(phone);

    setLoading(true);
    try {
      if (showVerifyStep) {
        await handleVerifyOtp(apiBase, apiPhone);
      } else if (isDirectSubFlow) {
        const { ti, ts } = getDirectSubTokens(
          SUBSCRIBE_BUTTON_ID,
          antifraudTi,
          antifraudTs
        );
        await handleDirectSubscribe(apiBase, apiPhone, ti, ts);
      } else {
        await handleManualSubscribe(apiBase, apiPhone);
      }
    } catch (error) {
      setErrorMsg(getErrorMessage(error, "request_failed"));
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DjzSignupPageDisplay
      className="djz-signup-page operatorSignup gamezoneSignup"
      bgImg={randomBg}
      dir={language === "ar" ? "rtl" : "ltr"}
    >
      {showError && (
        <FloatingAlert type="error">
          <Trans>{errorMsg}</Trans>
        </FloatingAlert>
      )}

      <div className="formsLayout formsLayoutjawal">
        <div className="header header_gamezone d-flex justify-content-between align-items-center">
          <Link to="/">
            <div className="logo gamezoneLogo" />
          </Link>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <select
              value={language}
              onChange={(e) => changeLanguage(e.target.value)}
              className="djz-lang-select"
              aria-label="Language"
            >
              <option value="ar">العربية</option>
              <option value="fr">Français</option>
              <option value="en">English</option>
            </select>
          </div>
        </div>

        <div className="links-Section links-Sectionjawwalgamezorid">
          <div className="play" />

          <form name="formsubmit" className="djz-form-wrap" onSubmit={handleSubscribe}>
            {!showVerifyStep ? (
              <>
                <select
                  className="input gamezoneInput djz-package-select"
                  value={packagePlan}
                  onChange={(e) => changePackagePlan(e.target.value)}
                  aria-label={t("package")}
                  disabled={loading || initializing}
                >
                  <option value="d">{t("Daily")}</option>
                  <option value="w">{t("Weekly")}</option>
                </select>

                <div className="djz-phone-row">
                  <input
                    type="text"
                    className="input gamezoneInput gamezonekeyph"
                    value="+213"
                    disabled
                  />
                  <input
                    type="tel"
                    className="input gamezoneInput gamezonenumph"
                    placeholder="XX XXX XX XX"
                    value={phone}
                    onChange={(e) =>
                      setPhone(toLocalPhone(e.target.value))
                    }
                    name="phone_number"
                    autoComplete="tel"
                    disabled={isDirectSubFlow || loading || initializing}
                    readOnly={isDirectSubFlow}
                  />
                </div>
              </>
            ) : (
              <div className="djz-verify-section">
                <p>
                  <Trans>verifycodeSub</Trans>
                </p>
                <input
                  type="text"
                  className="input gamezoneInput djz-otp-input"
                  value={otp}
                  placeholder={t("enterOtp")}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  autoComplete="one-time-code"
                  disabled={loading}
                />
              </div>
            )}

            <button
              id={SUBSCRIBE_BUTTON_ID}
              type="submit"
              className="btn djz-subscribe-btn"
              disabled={loading || initializing || (showVerifyStep && !otp)}
            >
              {loading || initializing
                ? t("loading")
                : showVerifyStep
                ? t("verify")
                : t("Subscribe")}
            </button>
          </form>

          <div className="footer jawwalgamezoridFooter zainiqFooter">
            <div className="ncellTos jawwalgamezoridncellTos">
              <p>
                {t("djz_terms_price", {
                  offerName: t(selectedPlan.offerNameKey),
                  price: selectedPlan.price,
                })}
              </p>
              <p>
                {t("djz_terms_sms_sub", {
                  sub: selectedPlan.sub,
                  shortcode: selectedPlan.shortcode,
                })}
              </p>
              <p>
                {t("djz_terms_sms_cancel", {
                  unsub: selectedPlan.unsub,
                  shortcode: selectedPlan.shortcode,
                })}
              </p>
              <p>
                <Trans
                  i18nKey="djz_terms_web_cancel"
                  components={[<Link to="/unsubscribe" />]}
                />
              </p>
              <p>
                <Trans>djz_terms_support</Trans>
              </p>
            </div>
          </div>
        </div>
      </div>
    </DjzSignupPageDisplay>
  );
}

export default withTranslation()(DjzSignupPage);
